import { Row, Table } from '@tanstack/react-table';

/**
 * Behavioural helpers for DataTable tree mode. Kept separate from the UI
 * cells so both the leading chevron (<TreeCellLeading>) and the table core
 * (row-body click, context menu, toolbar) route expansion changes through
 * one implementation.
 */

/** Explicit map of every expandable row id → true (across the whole tree). */
export function allExpandedMap<TData>(
  table: Table<TData>,
): Record<string, boolean> {
  return Object.fromEntries(
    table
      .getCoreRowModel()
      .flatRows.filter((r) => r.getCanExpand())
      .map((r) => [r.id, true]),
  );
}

/**
 * Whether every expandable row is expanded. Unlike tanstack's
 * getIsAllRowsExpanded, this ignores leaf rows (which are never "expanded"),
 * so a fully-open tree reads as all-expanded even though we use an explicit
 * id map rather than the `true` sentinel.
 */
export function isAllExpandableExpanded<TData>(table: Table<TData>): boolean {
  const expandable = table
    .getCoreRowModel()
    .flatRows.filter((r) => r.getCanExpand());
  return expandable.length > 0 && expandable.every((r) => r.getIsExpanded());
}

/** This row + every expandable descendant's id (depth-first). */
function expandableSubtreeIds<TData>(row: Row<TData>): string[] {
  const ids: string[] = [];
  const walk = (r: Row<TData>) => {
    if (!r.getCanExpand()) return;
    ids.push(r.id);
    r.subRows.forEach(walk);
  };
  walk(row);
  return ids;
}

/**
 * Expand or collapse a row and every expandable descendant in a SINGLE
 * expansion-state update (one onExpandedChange dispatch) instead of one per
 * node.
 *
 * Normalizes the tanstack `true` ("all expanded") sentinel to an explicit id
 * map before editing, so collapsing a subtree still works after a global
 * expand-all — you can't delete ids out of a boolean.
 *
 * The table is read off the row's cell context so callers that only hold a
 * Row (e.g. the leading chevron) don't have to thread it through.
 */
export function setSubtreeExpanded<TData>(
  row: Row<TData>,
  expand: boolean,
): void {
  const table = row.getAllCells()[0]?.getContext().table as
    | Table<TData>
    | undefined;
  if (!table) return;
  const ids = expandableSubtreeIds(row);
  table.setExpanded((old) => {
    const base =
      old === true
        ? allExpandedMap(table)
        : { ...(old as Record<string, boolean>) };
    for (const id of ids) {
      if (expand) base[id] = true;
      else delete base[id];
    }
    return base;
  });
}
